import {Component} from './component';
import {fileUploadService} from '../services/file-upload-service';
import {showProcessingPopup} from '../services/file-processing-helpers';
/**
 * File Upload Modal Component
 * Handles file upload for Book Import and Chapter Replace functionality
 */
export class FileUploadModal extends Component {

    setup() {
        // Element references
        this.container = this.$el;
        this.popupEl = this.$refs.popup;
        this.modalTitle = this.$refs.modalTitle;
        this.fileInput = this.$refs.fileInput;
        this.fileInputLabel = this.$refs.fileInputLabel;
        this.uploadButton = this.$refs.uploadButton;
        this.cancelButton = this.$refs.cancelButton;
        this.filePreview = this.$refs.filePreview;
        this.progressContainer = this.$refs.progressContainer;
        this.progressBar = this.$refs.progressBar;
        this.progressText = this.$refs.progressText;
        this.errorContainer = this.$refs.errorContainer;
        this.errorText = this.$refs.errorText;

        // State
        this.entityType = null;
        this.entityId = null;
        this.selectedFile = null;
        this.isUploading = false;

        // Bind event handlers
        this.bindEvents();
    }

    /**
     * @returns {Popup}
     */
    getPopup() {
        return window.$components.firstOnElement(this.popupEl, 'popup');
    }

    /**
     * Show modal
     */
    show(action, entityType, entityId) {
        this.entityType = entityType;
        this.entityId = entityId;

        // Set title
        const title = action === 'import-file' ? '📥 Import File' : '🔄 Replace File';
        this.modalTitle.textContent = title;

        // Reset state
        this.resetState();

        // Show popup
        this.getPopup().show();
    }

    /**
     * Hide modal
     */
    hide() {
        this.getPopup().hide();
    }

    bindEvents() {
        // File input change
        this.fileInput.addEventListener('change', this.onFileSelected.bind(this));

        // Drag and drop
        this.fileInputLabel.addEventListener('dragover', this.onDragOver.bind(this));
        this.fileInputLabel.addEventListener('dragleave', this.onDragLeave.bind(this));
        this.fileInputLabel.addEventListener('drop', this.onFileDropped.bind(this));

        // Upload button
        this.uploadButton.addEventListener('click', this.onUpload.bind(this));
    }

    /**
     * Reset modal state
     */
    resetState() {
        this.selectedFile = null;
        this.isUploading = false;
        this.fileInput.value = '';
        this.progressContainer.classList.add('hidden');
        this.errorContainer.classList.add('hidden');
        this.fileInputLabel.classList.remove('drag-over');
        this.updateFilePreview(null);
        this.updateUploadButton();
    }

    /**
     * Handle file selection from input
     */
    onFileSelected(e) {
        const file = e.target.files[0];
        if (file) {
            this.selectFile(file);
        }
    }

    /**
     * Handle drag over
     */
    onDragOver(e) {
        e.preventDefault();
        this.fileInputLabel.classList.add('drag-over');
    }

    /**
     * Handle drag leave
     */
    onDragLeave(e) {
        e.preventDefault();
        this.fileInputLabel.classList.remove('drag-over');
    }

    /**
     * Handle file drop
     */
    onFileDropped(e) {
        e.preventDefault();
        this.fileInputLabel.classList.remove('drag-over');

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            this.fileInput.files = files;
            this.selectFile(file);
        }
    }

    /**
     * Select and validate file
     */
    selectFile(file) {
        this.hideError();

        // Validate file
        const validation = this.validateFile(file);
        if (!validation.valid) {
            this.showError(validation.error);
            return;
        }

        this.selectedFile = file;
        this.updateFilePreview(file);

        // Auto-upload immediately after file selection
        this.onUpload();
    }

    /**
     * Validate file
     */
    validateFile(file) {
        // Check file size (max 50MB)
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            return {
                valid: false,
                error: 'File too large (max 50MB)'
            };
        }

        // Check file type
        const allowedTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
            'application/msword', // .doc
        ];

        const allowedExtensions = ['.pdf', '.docx', '.doc'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            return {
                valid: false,
                error: 'File type not supported. Please upload PDF, DOCX, or DOC files.'
            };
        }

        return { valid: true };
    }

    /**
     * Update file preview
     */
    updateFilePreview(file) {
        const preview = this.$refs.filePreview;

        if (!file) {
            preview.innerHTML = '';
            return;
        }

        const fileSize = this.formatFileSize(file.size);
        const fileIcon = this.getFileIcon(file);

        preview.innerHTML = `
            <div class="file-preview-item">
                <span class="file-preview-icon">${fileIcon}</span>
                <div class="file-preview-info">
                    <div class="file-preview-name">${file.name}</div>
                    <div class="file-preview-size">${fileSize}</div>
                </div>
            </div>
        `;
    }

    /**
     * Get file icon based on type
     */
    getFileIcon(file) {
        const name = file.name.toLowerCase();

        if (name.endsWith('.pdf')) return '📄';
        if (name.endsWith('.docx') || name.endsWith('.doc')) return '📝';
        if (name.endsWith('.md')) return '📑';
        if (name.endsWith('.txt')) return '📋';

        return '📎';
    }

    /**
     * Format file size
     */
    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Update upload button state
     */
    updateUploadButton() {
        if (this.selectedFile && !this.isUploading) {
            this.uploadButton.disabled = false;
            this.uploadButton.textContent = 'Upload';
        } else {
            this.uploadButton.disabled = true;
            this.uploadButton.textContent = this.isUploading ? 'Uploading...' : 'Select a file';
        }
    }

    /**
     * Handle upload
     */
    async onUpload() {
        if (!this.selectedFile || this.isUploading) return;

        this.isUploading = true;
        this.updateUploadButton();
        this.hideError();
        this.showProgress(0);

        try {
            // Upload file
            const jobId = await fileUploadService.uploadFile(
                this.selectedFile,
                this.entityType,
                this.entityId,
                (progress) => this.updateProgress(progress)
            );

            // Close upload modal
            this.hide();

            // Show processing popup
            showProcessingPopup(jobId, this.entityType);

        } catch (error) {
            console.error('Upload error:', error);
            this.showError(error.message || 'Failed to upload file');
            this.hideProgress();
            this.isUploading = false;
            this.updateUploadButton();
        }
    }

    /**
     * Show progress bar
     */
    showProgress(progress) {
        this.progressContainer.classList.remove('hidden');
        this.progressBar.style.width = `${progress}%`;
        this.progressText.textContent = `${Math.round(progress)}%`;
    }

    /**
     * Hide progress bar
     */
    hideProgress() {
        this.progressContainer.classList.add('hidden');
    }

    /**
     * Show error message
     */
    showError(message) {
        this.errorText.textContent = message;
        this.errorContainer.classList.remove('hidden');
    }

    /**
     * Hide error message
     */
    hideError() {
        this.errorContainer.classList.add('hidden');
    }
}